/**
 * Sign-in: password, then the authenticator code when MFA is on (M02-04).
 * The privacy notice sits under the form, as RA 10173 asks.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, KeyRound } from 'lucide-react';
import { useAuth, type SessionUser } from '../lib/auth';
import { ApiError } from '../lib/api';
import { Alert, Button, Card, Field, inputClass } from '../components/ui';
import { AuthLayout } from './AuthLayout';

export function landingFor(user: SessionUser): string {
  if (user.mustResetPassword) return '/change-password';
  if (!user.mfaEnabled) return '/mfa-setup';
  // A deep link opened while signed out (e.g. an app's OAuth consent) comes back here.
  try {
    const back = sessionStorage.getItem('cx_return');
    if (back?.startsWith('/')) {
      sessionStorage.removeItem('cx_return');
      return back;
    }
  } catch {
    /* storage unavailable */
  }
  return '/';
}

export function LoginPage(): JSX.Element {
  const { signIn, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const fail = (caught: unknown): void =>
    setError(caught instanceof ApiError ? caught.message : 'Could not sign in. Check your connection and try again.');

  const onPassword = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await signIn(email.trim(), password);
      if (result.kind === 'mfa') setMfaToken(result.mfaToken);
      else navigate(landingFor(result.user), { replace: true });
    } catch (caught) {
      fail(caught);
    } finally {
      setBusy(false);
    }
  };

  const onCode = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!mfaToken) return;
    setError(null);
    setBusy(true);
    try {
      const user = await verifyMfa(mfaToken, code.trim());
      navigate(landingFor(user), { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401 && caught.message.includes('Start')) setMfaToken(null);
      fail(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      {mfaToken ? (
        <Card title="Enter your code" description="Open your authenticator app and type the 6-digit code for ITCSI Barangay.">
          <form className="space-y-4" onSubmit={(e) => void onCode(e)} noValidate>
            {error && <Alert tone="critical">{error}</Alert>}
            <Field label={useRecovery ? 'Recovery code' : 'Authenticator code'} required>
              <input
                className={`${inputClass} text-center text-lg tracking-[0.3em]`}
                inputMode={useRecovery ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                maxLength={useRecovery ? 9 : 6}
                placeholder={useRecovery ? 'XXXX-XXXX' : '000000'}
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(useRecovery ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, ''))}
              />
            </Field>
            <Button type="submit" busy={busy} className="w-full min-h-11">
              <KeyRound aria-hidden="true" className="h-4 w-4" /> Verify and sign in
            </Button>
            <div className="flex justify-between text-sm">
              <button type="button" className="text-brand-700 hover:underline dark:text-brand-100" onClick={() => { setUseRecovery((v) => !v); setCode(''); }}>
                {useRecovery ? 'Use the authenticator app' : 'Lost your phone? Use a recovery code'}
              </button>
              <button type="button" className="text-slate-500 hover:underline" onClick={() => { setMfaToken(null); setCode(''); setError(null); }}>
                Back
              </button>
            </div>
          </form>
        </Card>
      ) : (
        <Card title="Sign in" description="Use the email address your barangay registered for you.">
          <form className="space-y-4" onSubmit={(e) => void onPassword(e)} noValidate>
            {error && <Alert tone="critical">{error}</Alert>}
            <Field label="Email address" required>
              <input className={inputClass} type="email" autoComplete="username" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field label="Password" required>
              <div className="relative">
                <input
                  className={`${inputClass} pr-11`}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute right-1 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-lg text-slate-500 hover:text-slate-800 dark:hover:text-white"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? <EyeOff aria-hidden="true" className="h-4 w-4" /> : <Eye aria-hidden="true" className="h-4 w-4" />}
                </button>
              </div>
            </Field>
            <Button type="submit" busy={busy} className="w-full min-h-11">
              Sign in
            </Button>
            <p className="text-center text-sm text-slate-500">
            </p>
          </form>
          <p className="mt-5 text-xs leading-relaxed text-slate-500">
            ITCSI Barangay processes your name, email and sign-in activity to secure your account and keep the audit trail
            the law requires, under the Data Privacy Act of 2012 (RA 10173). Your barangay is the personal information
            controller; ITCORE Solutions Inc. processes the data on its behalf.
          </p>
        </Card>
      )}
    </AuthLayout>
  );
}
